import { Container, Sprite, Text, TextStyle } from "pixi.js";
import { FadeScreen } from "../../../../game/components/FadeScreen";
import { RaidResourcePackage } from "../../resources/RaidResourcePackage";
import { ProfileContainerWithHint } from "../../../../common/components/card/ProfileContainerWithHint";
import { CommonResourcesPackage } from "../../../../common/resources/CommonResourcesPackage";
import { BuildingInfo } from "./BuildingInfo";
import { gsap } from "gsap";
import { RaidCard } from "../../../../game/flow/impl/diceActions/raidAction/RaidCard";
import { ICanvasScaler } from "../../../../../../canvas/ICanvasScaler";
import { UserProfileResourcePackage } from "../../../userprofile/resources/UserProfileResourcePackage";
import { NoCacheDynamicPackage } from "../../../../common/resources/NoCacheDynamicPackage";

export class Card extends Container {
    private readonly commonResources: CommonResourcesPackage;
    private readonly raidResources: RaidResourcePackage;
    private readonly userProfileResources: UserProfileResourcePackage;
    private fadeScreens: Array<FadeScreen>;
    private cardContent: Container;
    private cardBackground: Sprite;
    private profileContainer: ProfileContainerWithHint;
    private buildingInfo: BuildingInfo;
    private level: Text;
    private building: Sprite;
    private canvasScaler: ICanvasScaler;

    public constructor(
        commonResources: CommonResourcesPackage,
        raidResources: RaidResourcePackage,
        userProfileResources: UserProfileResourcePackage,
        canvasScaler: ICanvasScaler
    ) {
        super();
        this.commonResources = commonResources;
        this.raidResources = raidResources;
        this.userProfileResources = userProfileResources;
        this.canvasScaler = canvasScaler;
        this.fadeScreens = new Array<FadeScreen>();
        this.build();
        this.once("added", this.hideCardContent.bind(this));
    }

    private build(): void {
        this.fadeScreens.push(this.fadeScreenFactory());
        this.fadeScreens.push(this.fadeScreenFactory());
        this.addChild(this.fadeScreens[0]);
        this.cardBackground = new Sprite(this.raidResources.assets_views_raid_ui_raid_back_png);
        this.cardBackground.anchor.set(0.5, 0.5);
        this.addChild(this.cardBackground);
        this.cardContent = new Container();
        this.addChild(this.cardContent);
        this.building = new Sprite(this.raidResources.assets_views_raid_ui_Building_0005_png);
        this.building.anchor.set(0.5, 0.5);
        this.cardContent.addChild(this.building);
        this.level = new Text("RANK.0", new LevelTextStyle());
        this.level.anchor.set(0.5, 0.5);
        this.level.angle = -5;
        this.level.position.y = -this.cardBackground.height * 0.32;
        this.cardContent.addChild(this.level);
        this.cardContent.addChild(this.fadeScreens[1]);
    }

    private hideCardContent(): void {
        this.fadeScreens[0].visible = false;
        this.fadeScreens[1].visible = false;
        this.cardContent.visible = false;
    }

    public updateAndShowCardContent(raidCardData: RaidCard, raidDynamicResources: NoCacheDynamicPackage): void {
        this.cardBackground.texture = this.raidResources.assets_views_raid_ui_raid_front_png;
        this.profileContainer = new ProfileContainerWithHint(
            this.userProfileResources,
            raidDynamicResources.resource(raidCardData.guest.avatar),
            raidCardData.guest.firstName,
            this.commonResources.assets_common_ui_hint_arrow_left_png
        );
        this.profileContainer.position.set(0, -this.cardBackground.height * 0.4);
        this.cardContent.addChild(this.profileContainer);
        this.building.texture = raidDynamicResources.resource(raidCardData.building.imageUrl);
        this.buildingInfo = new BuildingInfo(
            raidCardData.building.address,
            this.commonResources.assets_common_ui_hint_arrow_left_png,
            this.cardBackground.width
        );
        this.buildingInfo.position.set(0, this.cardBackground.height * 0.4)
        this.cardContent.addChild(this.buildingInfo);
        this.level.text = `RANK.${raidCardData.building.level}`;
        this.cardContent.visible = true;
    }

    public fadeIn(): void {
        this.fadeScreens[0].visible = true;
        this.fadeScreens[0].fadeIn();
    }

    public getTutorialAnimations(): gsap.core.Timeline {
        const animationTimeLine = new gsap.core.Timeline();
        animationTimeLine.set(this.cardContent, { visible: true });
        animationTimeLine.set(this.fadeScreens[0], { visible: true });
        animationTimeLine.set(this.fadeScreens[1], { visible: true });
        animationTimeLine.fromTo(this.fadeScreens[0], { alpha: 0 }, { alpha: 0.7, duration: 0.3 });
        animationTimeLine.add(this.profileContainer.getHintAnimation());
        animationTimeLine.fromTo(this.fadeScreens[1], { alpha: 0 }, { alpha: 0.6, duration: 0.3, yoyo: true, repeat: 1, repeatDelay: 1, ease: "none" }, "<10%");
        animationTimeLine.add(this.buildingInfo.getHintAnimation());
        animationTimeLine.fromTo(this.fadeScreens[1], { alpha: 0 }, { alpha: 0.6, duration: 0.3, yoyo: true, repeat: 1, repeatDelay: 1, ease: "none" }, "<10%");
        animationTimeLine.then(() => animationTimeLine.kill());
        return animationTimeLine;
    }

    private fadeScreenFactory(): FadeScreen {
        const width = this.canvasScaler.referenceWidth;
        const height = this.canvasScaler.referenceHeight;
        const fadeScreen = new FadeScreen(0.3, 0, 0.7, width, height);
        fadeScreen.visible = false;
        fadeScreen.alpha = 0;
        return fadeScreen;
    }

    public layout(width: number, height: number): void {
        this.fadeScreens.forEach(fadeScreen => {
            fadeScreen.position.set(-width / 2, -height / 2);
            fadeScreen.width = width;
            fadeScreen.height = height;
        })
    }
}

class LevelTextStyle extends TextStyle {
    public constructor() {
        super(new TextStyle({
            dropShadow: true,
            dropShadowAlpha: 0.4,
            dropShadowAngle: 0,
            dropShadowBlur: 2,
            dropShadowDistance: 2,
            fill: "#f7ad2d",
            fontFamily: "COMICZ",
            fontSize: 58
        }));
    }
}
